import { useCallback, useMemo, useState } from 'react';
import type React from 'react';
import { Button, Modal, Space, message } from 'antd';
import { ExclamationCircleOutlined, ExportOutlined, ImportOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ExportModal } from '@/components/shared/ExportModal';
import { ImportWizardModal } from '@/components/shared/ImportWizardModal';
import { PageShell } from '@/components/PageShell/PageShell';
import { ERPTable } from '@/components/DataTable/ERPTable';
import { useProductList } from '@/hooks/useProductList';
import { useCloneProduct, useDeleteProduct, useBulkUpdateProductState } from '@/hooks/useProductMutations';
import { useCategoryList } from '@/hooks/useCategories';
import { useTableState } from '@/hooks/useTableState';
import { useRbac } from '@/hooks/useRbac';
import { ROUTES } from '@/constants/routes';
import type { ProductListQuery } from '@/services/queryKeys';
import { normalizeText } from '@/utils/normalize';
import { buildProductColumns, PRODUCT_TABLE_KEY } from './parts/columns';
import { PRODUCT_STATE_LABEL } from './parts/constants';
import { ProductFilters } from './parts/filters';
import { BulkToolbar } from './components/BulkToolbar';
import { ExportProductButton } from './components/ExportProductButton';

export function ProductsPage() {
  const navigate = useNavigate();
  const { hasAny } = useRbac();
  const { page, pageSize, setTableState } = useTableState({ page: 1, pageSize: 20 });
  const [filter, setFilter] = useState<Omit<ProductListQuery, 'page' | 'page_size'>>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const cloneMutation = useCloneProduct();
  const deleteMutation = useDeleteProduct();
  const bulkStateMutation = useBulkUpdateProductState();

  const query: ProductListQuery = useMemo(
    () => ({ ...filter, page, page_size: pageSize }),
    [filter, page, pageSize],
  );

  const { data, isLoading } = useProductList(query);
  const { data: categoryData } = useCategoryList({});

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categoryData?.data?.forEach((c) => map.set(c._id, c.name));
    return map;
  }, [categoryData]);

  // Client-side search: trim, case-insensitive, diacritics-insensitive
  const searchQuery = (filter.q ?? '').trim();
  const filteredData = useMemo(() => {
    const rows = data?.data ?? [];
    if (!searchQuery) return rows;
    return rows.filter((row) => {
      const name = normalizeText(row.name ?? '');
      const sku = normalizeText(row.sku ?? '');
      const q = normalizeText(searchQuery);
      return name.includes(q) || sku.includes(q);
    });
  }, [data?.data, searchQuery]);

  const handleClone = useCallback(
    async (id: string) => {
      try {
        const result = await cloneMutation.mutateAsync(id);
        void message.success('Đã nhân bản sản phẩm');
        navigate(ROUTES.products.edit(result._id));
      } catch {
        void message.error('Nhân bản thất bại');
      }
    },
    [cloneMutation, navigate],
  );

  const handleDelete = useCallback(
    (id: string, name: string) => {
      Modal.confirm({
        title: 'Xoá sản phẩm',
        icon: <ExclamationCircleOutlined />,
        content: `Bạn muốn xoá sản phẩm "${name}"? Sản phẩm sẽ được ẩn khỏi hệ thống.`,
        okText: 'Xoá',
        okType: 'danger',
        cancelText: 'Huỷ',
        onOk: async () => {
          try {
            await deleteMutation.mutateAsync(id);
            void message.success('Đã xoá sản phẩm');
          } catch {
            void message.error('Xoá thất bại');
          }
        },
      });
    },
    [deleteMutation],
  );

  const columns = useMemo(
    () => buildProductColumns({ categoryMap, onClone: handleClone, onDelete: handleDelete, navigate }),
    [categoryMap, handleClone, handleDelete, navigate],
  );

  return (
    <PageShell
      title="Sản phẩm"
      extra={
        <Space>
          <Button icon={<ImportOutlined />} onClick={() => setImportOpen(true)}>
            Import Excel
          </Button>
          <Button icon={<ExportOutlined />} onClick={() => setExportOpen(true)}>
            Xuất dữ liệu
          </Button>
          <ExportProductButton filter={filter} />
          {hasAny(['products.create']) && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(ROUTES.products.new)}>
              Thêm sản phẩm
            </Button>
          )}
        </Space>
      }
      filters={<ProductFilters value={query} onChange={(next) => setFilter(next)} />}
    >
      {selectedRowKeys.length > 0 && (
        <BulkToolbar
          count={selectedRowKeys.length}
          loading={bulkStateMutation.isPending}
          onStateChange={async (state) => {
            try {
              const ids = selectedRowKeys.filter((k): k is string => typeof k === 'string');
              await bulkStateMutation.mutateAsync({ ids, state });
              const stateLabel = PRODUCT_STATE_LABEL[state]?.toLowerCase() ?? 'cập nhật';
              void message.success(`Sản phẩm đã được ${stateLabel} thành công`);
              setSelectedRowKeys([]);
            } catch {
              void message.error('Cập nhật thất bại');
            }
          }}
          onClear={() => setSelectedRowKeys([])}
        />
      )}
      <ERPTable
        tableKey={PRODUCT_TABLE_KEY}
        rowKey="_id"
        loading={isLoading}
        dataSource={filteredData}
        columns={columns}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
        pagination={{
          current: page,
          pageSize,
          total: filteredData.length,
          showSizeChanger: true,
          showTotal: (t) => `${t} sản phẩm`,
          onChange: (p, ps) => setTableState({ page: p, pageSize: ps }),
        }}
      />
      <ImportWizardModal entity="products" open={importOpen} onClose={() => setImportOpen(false)} />
      <ExportModal entity="products" open={exportOpen} onClose={() => setExportOpen(false)} />
    </PageShell>
  );
}
